// AdminAllNotifications.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./AdminAllNotifications.module.scss";
import { timeAgo } from "@/utils/timeAgo";

const TYPE_LABELS = {
  general: "General",
  new_user: "New User",
  membership_renewed: "Membership Renewed",
  new_member_profile: "New Member Profile",
  wallet_refill: "Wallet Refill",
  club_join_request_received: "Club Join Request Received",
  club_join_request_accepted: "Club Join Request Accepted",
  club_join_request_rejected: "Club Join Request Rejected",
};

export default function AdminAllNotifications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filterRead, setFilterRead] = useState("all"); // all | unread | read
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const navigate = useNavigate();

  const query = useMemo(() => {
    const q = new URLSearchParams();
    q.set("page", String(page));
    q.set("limit", String(limit));
    if (filterRead === "unread") q.set("isRead", "false");
    if (filterRead === "read") q.set("isRead", "true");
    return q.toString();
  }, [page, limit, filterRead]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setErr(null);
      const res = await api.get(`/v1/admin/layout/all-notifications?${query}`);
      setItems(res.data?.items ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (e) {
      console.error("Failed to load notifications:", e);
      setErr("Couldn't load notifications.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const pages = Math.max(1, Math.ceil(total / limit));

  const markRead = async (id) => {
    try {
      await api.patch(`/v1/admin/layout/notification/${id}/read`);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch (e) {
      console.error("Mark read failed:", e);
    }
  };

  const openCta = async (n) => {
    if (!n.ctaPath) return;
    // mark read, then navigate
    if (!n.isRead) await markRead(n.id);
    navigate(n.ctaPath);
  };

  const onChangeFilter = (e) => {
    setPage(1);
    setFilterRead(e.target.value);
  };

  const onChangeLimit = (e) => {
    setPage(1);
    setLimit(parseInt(e.target.value, 10));
  };

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div className={cls.headLeft}>
          <h1 className={cls.title}>All Notifications</h1>
          <p className={cls.sub}>Everything happening across admin actions.</p>
        </div>

        <div className={cls.controls}>
          <label className={cls.control}>
            <span>Status</span>
            <select value={filterRead} onChange={onChangeFilter}>
              <option value="all">All</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
            </select>
          </label>

          <label className={cls.control}>
            <span>Per page</span>
            <select value={limit} onChange={onChangeLimit}>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
        </div>
      </header>

      {err && <div className={cls.error}>{err}</div>}

      <div className={cls.tableWrap} aria-busy={loading ? "true" : "false"}>
        <table className={cls.table}>
          <thead>
            <tr>
              <th>Notification</th>
              <th className={cls.thActions}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              [...Array(7)].map((_, i) => (
                <tr key={`sk-${i}`} className={cls.skelRow}>
                  <td colSpan={2}>
                    <div className={cls.skel} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={2} className={cls.empty}>
                  No notifications found.
                </td>
              </tr>
            ) : (
              items.map((n) => {
                const label = TYPE_LABELS[n.type] || n.type;

                return (
                  <tr key={n.id} data-read={n.isRead ? "1" : "0"}>
                    <td className={cls.mainCell}>
                      <div className={cls.notif}>
                        <div className={cls.topLine}>
                          <span
                            className={`${cls.notifTitle} ${
                              !n.isRead ? cls.unread : ""
                            }`}
                            title={n.title}
                          >
                            {n.title}
                          </span>

                          <span className={cls.meta}>
                            <span
                              className={`${cls.type} ${
                                cls[`type--${n.type}`] || cls["type--general"]
                              }`}
                              title={n.type}
                            >
                              {label}
                            </span>
                            <span className={cls.dot} aria-hidden="true">
                              •
                            </span>
                            <span className={cls.time}>
                              {timeAgo(n.createdAt)}
                            </span>
                          </span>
                        </div>

                        {n.description ? (
                          <div className={cls.desc}>{n.description}</div>
                        ) : null}
                      </div>
                    </td>

                    <td className={cls.actions}>
                      {n.ctaPath ? (
                        <button
                          type="button"
                          className={cls.ctaBtn}
                          onClick={() => openCta(n)}
                          aria-label={n.ctaLabel || "Open"}
                          title={n.ctaLabel || "Open"}
                        >
                          <span className={cls.ctaText}>
                            {n.ctaLabel || "Open"}
                          </span>
                          <span className={cls.ctaArrow} aria-hidden="true">
                            →
                          </span>
                        </button>
                      ) : (
                        <span className={cls.noCta}>—</span>
                      )}

                      {!n.isRead ? (
                        <button
                          type="button"
                          className={cls.readIconBtn}
                          onClick={() => markRead(n.id)}
                          aria-label="Mark as read"
                          title="Mark as read"
                        >
                          ✓
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <footer className={cls.footer}>
        <div className={cls.pagination}>
          <button
            type="button"
            className={cls.pageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            ← Prev
          </button>

          <span className={cls.pageInfo}>
            Page <strong>{page}</strong> of <strong>{pages}</strong>{" "}
            <em className={cls.total}>(Total: {total})</em>
          </span>

          <button
            type="button"
            className={cls.pageBtn}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages || loading}
          >
            Next →
          </button>
        </div>
      </footer>
    </div>
  );
}
